import { useEffect, useRef } from 'react';
import type { ConversationMode } from '@/types/chat';

interface UseConversationModeSyncArgs {
  /**
   * The mode reported by the backend's cached conversations query.
   * `undefined` while the query is loading or when the current
   * conversationId isn't yet present in the list.
   */
  conversationMode: ConversationMode | undefined;
  /** Current local mode (driven by SSE handlers + initial state). */
  mode: ConversationMode;
  /** Setter from useChat. */
  setMode: (next: ConversationMode) => void;
}

/**
 * Sync the conversation's backend-reported mode into local state ONLY
 * when the backend value actually changes. This prevents the
 * `useQuery(['conversations'])` cache (up to 30s stale) from clobbering
 * SSE-driven local mode flips between refetches.
 *
 * Why a plain `useEffect(..., [conversationMode, mode])` is wrong:
 *   1. SSE arrives, useChat handler calls setMode('human_handoff')
 *   2. effect fires because mode changed
 *   3. cached conversationMode is still 'ai' (refetch hasn't fired)
 *   4. effect calls setMode('ai') — reverts the SSE-driven flip
 *
 * Why this is right: we only react when `conversationMode` itself
 * changes (a real backend transition reflected in cache), not on
 * every local mode change. Local SSE-driven changes are "winning"
 * between refetches; the cache catches up later and confirms.
 */
export function useConversationModeSync({
  conversationMode,
  mode,
  setMode,
}: UseConversationModeSyncArgs): void {
  const previousConversationModeRef = useRef<ConversationMode | undefined>(undefined);

  useEffect(() => {
    if (!conversationMode) return;
    if (conversationMode === previousConversationModeRef.current) return;

    previousConversationModeRef.current = conversationMode;

    if (conversationMode !== mode) {
      setMode(conversationMode);
    }
    // We intentionally exclude `mode` from deps — the effect should
    // only fire when the BACKEND mode changes, not when local mode
    // flips via SSE.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationMode, setMode]);
}
